import { PATH_DICTS_AGREEMENT_TYPES } from "@/constants";
import { fetchAgreementTypeList } from "@/services/agreementType";
import { forwardRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AsyncSelect, { Option, type AsyncSelectBaseProps } from "./AsyncSelect";
import appConfiguration from "./cfg/AppCfg";
import { ExternalLink } from "lucide-react";
import { FilterType, mergeFilters } from "@/lib/filters";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { useCreateDataTableOptions } from "./data-table/hooks/useCreateDataTableOptions";

import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { AgreementType } from "@/types/agreementType";

interface AgreementTypeSelectProps extends AsyncSelectBaseProps {
  className?: string;
  inDialog?: boolean;
  clear?: boolean;
  showLink?: boolean;
  placeholder?: string;
  defaultItem?: Partial<Option>;
}

export default forwardRef<HTMLButtonElement, AgreementTypeSelectProps>(
  function AgreementTypeSelect(
    { value, inDialog, showLink = true, defaultItem, ...props },
    ref,
  ) {
    const { t } = useTranslation();
    const name = "agreementTypeSelect";
    const options = useCreateDataTableOptions(name);
    const queryKey = useDynamicQueryParams<AgreementType>(name, options);

    const onFilter = useCallback(
      (filterValue: string) => {
        options.setFilterValue("name", {
          value: filterValue,
          filter: FilterType.IncludesString,
        });
      },
      [options],
    );

    const filters = useMemo(
      () =>
        mergeFilters(options.filters, [
          {
            id: "active",
            value: {
              filter: FilterType.EqualsString,
              value: "true",
            },
          },
        ]),
      [options.filters],
    );

    return (
      <span className="flex items-start gap-1">
        <span className="flex-1">
          <AsyncSelect
            queryKey={queryKey}
            queryFn={() =>
              fetchAgreementTypeList({
                filters,
                limit: appConfiguration.selectComponentsPaginationLimit,
              })
            }
            placeholder={t("agreementTypeSelect.placeholder")}
            loadingLabel={t("agreementTypeSelect.loadingAgreementTypes")}
            noResultsLabel={t("agreementTypeSelect.noResults")}
            searchLabel={t("agreementTypeSelect.search")}
            enableFilter={true}
            onFilter={onFilter}
            className="w-full"
            {...props}
            value={value}
            ref={ref}
            inDialog={inDialog}
            defaultItem={defaultItem}
          />
        </span>
        {showLink && (
          <Link to={PATH_DICTS_AGREEMENT_TYPES}>
            <Button variant="ghost" size="icon" type="button">
              <ExternalLink className="w-4 h-4" />
            </Button>
          </Link>
        )}
      </span>
    );
  },
);
